import { HTMLAttributes, createElement } from 'react'
import { twMerge } from 'tailwind-merge'
import {
    FontSizes,
    FontWeights,
    LetterSpacings,
    LineHeights,
    TextColors,
} from 'types'

const DS_MAP = {
    heading: {
        xl: {
            size: 'text-[21px]/[30px]',
            letterSpacing: 'tracking-[0.003em]',
        },
    },
    title: {
        xs: {
            size: 'text-xs/[16px]',
        },
        hero: {
            size: 'text-[1.75rem]/[2.75rem]',
        },
        large: {
            size: 'text-[21px]/7',
        },
        moderate: {
            size: 'text-lg/6',
        },
        small: {
            size: 'text-base/5',
        },
        tiny: {
            size: 'text-sm/5',
        },
    },
    body: {
        moderate: {
            size: 'text-base/5',
        },
        s: {
            size: 'text-sm/[21px]',
            letterSpacing: 'tracking-[0.015em]',
        },
        xs: {
            size: 'text-xs/5',
            letterSpacing: 'tracking-[0.015em]',
        },
    },
    caption: {
        moderate: {
            size: 'text-[13px]/4',
        },
        small: {
            size: 'text-xs',
        },
    },
    label: {
        xs: {
            size: 'text-xs/[16px]',
        },
        s: {
            size: 'text-sm/[22px]',
        },
    },
} satisfies {
    [k: string]: {
        [v: string]: {
            size: FontSizes
            letterSpacing?: LetterSpacings
        }
    }
}

interface TypographyProps
    extends HTMLAttributes<
        | HTMLHeadingElement
        | HTMLAnchorElement
        | HTMLParagraphElement
        | HTMLSpanElement
    > {
    readonly as?:
        | 'p'
        | 'a'
        | 'h1'
        | 'h2'
        | 'h3'
        | 'h4'
        | 'h5'
        | 'h6'
        | 'b'
        | 'i'
        | 'em'
        | 'span'
    readonly size?: FontSizes
    readonly weight?: FontWeights
    readonly lineHeight?: LineHeights
    readonly spacing?: LetterSpacings
    readonly color?: TextColors
}

function Typography({
    as = 'p',
    size,
    weight,
    lineHeight,
    spacing,
    color,
    className,
    children,
    ...props
}: TypographyProps): JSX.Element {
    return createElement(
        as,
        {
            ...props,
            className: twMerge(
                size ?? '',
                weight ?? '',
                lineHeight ?? '',
                spacing ?? '',
                color ?? '',
                className
            ),
        },
        children
    )
}

interface HeadingProps {
    readonly variant?: keyof (typeof DS_MAP)['heading']
}

function Heading({
    as = 'h2',
    variant = 'xl',
    weight = 'font-semibold',
    color = 'text-primary',
    size,
    spacing,
    ...props
}: HeadingProps & TypographyProps): JSX.Element {
    const _size = size ?? DS_MAP.heading[variant].size
    const _spacing = spacing ?? DS_MAP.heading[variant].letterSpacing

    return (
        <Typography
            {...props}
            as={as}
            size={_size}
            weight={weight}
            color={color}
            spacing={_spacing}
        />
    )
}

interface TitleProps {
    readonly variant?: keyof (typeof DS_MAP)['title']
    readonly isBreakCenter?: boolean
    readonly isBreakEnd?: boolean
}

function Title({
    as = 'h3',
    variant = 'moderate',
    weight = 'font-bold',
    size,
    isBreakCenter,
    isBreakEnd,
    className,
    ...props
}: TitleProps & TypographyProps): JSX.Element {
    const _size = size ?? DS_MAP.title[variant].size

    const layoutClasses = twMerge(
        isBreakCenter ? 'text-center break-words block w-full' : '',
        isBreakEnd ? 'text-end break-words flex-1 min-w-0 w-full' : ''
    )

    return (
        <Typography
            {...props}
            as={as}
            size={_size}
            weight={weight}
            className={twMerge(layoutClasses, className)}
        />
    )
}

interface BodyProps {
    readonly variant?: keyof (typeof DS_MAP)['body']
}

function Body({
    as = 'p',
    variant = 's',
    size,
    spacing,
    ...props
}: BodyProps & TypographyProps): JSX.Element {
    const _size = size ?? DS_MAP.body[variant].size
    const _spacing =
        spacing ?? (DS_MAP.body[variant] as typeof DS_MAP.body.s).letterSpacing

    return <Typography {...props} as={as} size={_size} spacing={_spacing} />
}

interface CaptionProps {
    readonly variant?: keyof (typeof DS_MAP)['caption']
}

function Caption({
    variant = 'moderate',
    size,
    ...props
}: CaptionProps & TypographyProps): JSX.Element {
    const _size = size ?? DS_MAP.caption[variant].size

    return <Typography {...props} size={_size} />
}

interface LabelProps {
    readonly variant?: keyof (typeof DS_MAP)['label']
}

function Label({
    variant = 's',
    weight = 'font-semibold',
    size,
    ...props
}: LabelProps & TypographyProps): JSX.Element {
    const _size = size ?? DS_MAP.label[variant].size

    return <Typography {...props} size={_size} weight={weight} />
}

Typography.Heading = Heading
Typography.Title = Title
Typography.Body = Body
Typography.Caption = Caption
Typography.Label = Label

export { Typography }
